import React from 'react';

import { useFormContext } from 'react-hook-form';
import { InfoBox, LbInput, LbSelect, TextArea } from '@infoods/lifebase-ui';

import classes from './RecipeDetailsCard.module.scss';
import { LbNumericFormatInput } from '../../../../shared/components/form-components';
import { InfoTooltip } from '../../../../shared/components/ui-components';
import { Option } from '../../../../general-types';
import { RecipeFormValues } from '../interfaces/RecipeFormValues';
import { RecipeImageField } from './RecipeImageField/RecipeImageField';
import { validateWhitespace } from '../../../../shared/utils/reactHookFormUtils';

type RecipeDetailsCardProps = {
  servingMeasurementOptions: Option[];
  savedImageUrl: string | null;
};

const MIN_SERVINGS = 1;
const MAX_SERVINGS = 100;
const MIN_AMOUNT = 1;
const MAX_AMOUNT = 9999;

const servingsRangeMessage = `Value must be between ${MIN_SERVINGS} and ${MAX_SERVINGS}`;
const amountRangeMessage = `Value must be between ${MIN_AMOUNT} and ${MAX_AMOUNT}`;

const RecipeDetailsCard = ({
  servingMeasurementOptions,
  savedImageUrl,
}: RecipeDetailsCardProps) => {
  const { control } = useFormContext<RecipeFormValues>();

  const generalInputProps = {
    control,
    size: 'medium',
    variant: 'standard',
  } as const;

  return (
    <InfoBox
      title={
        <span className={classes.title}>
          Recipe Details
          <InfoTooltip arrow placement="right">
            Define Servings - (Example: If a Serving = 1 Cup, Set Measurement amount to 1
            and Measurement to Cup)
          </InfoTooltip>
        </span>
      }
      contentClassName={classes.box}
    >
      <RecipeImageField savedImageUrl={savedImageUrl} />

      <div className={classes.inputBox}>
        <LbInput
          {...generalInputProps}
          name="name"
          label="Recipe name"
          rules={{
            required: 'Recipe name is required',
            validate: { validateWhitespace },
          }}
        />
        <LbNumericFormatInput
          {...generalInputProps}
          name="servingSize"
          label="Servings"
          rules={{
            required: 'Servings is required',
            min: { value: MIN_SERVINGS, message: servingsRangeMessage },
            max: { value: MAX_SERVINGS, message: servingsRangeMessage },
          }}
          allowNegative={false}
          decimalScale={0}
          selectValueOnFocus
        />
        <LbNumericFormatInput
          {...generalInputProps}
          name="amount"
          label="Measurement amount"
          rules={{
            required: 'Measurement amount is required',
            min: { value: MIN_AMOUNT, message: amountRangeMessage },
            max: { value: MAX_AMOUNT, message: amountRangeMessage },
          }}
          allowNegative={false}
          decimalScale={3}
          selectValueOnFocus
        />
        <LbSelect
          {...generalInputProps}
          name="servingMeasurement"
          label="Measurement"
          options={servingMeasurementOptions}
          rules={{ required: 'Measurement is required' }}
        />
        <div className={classes.fullRow}>
          <TextArea
            {...generalInputProps}
            name="description"
            label="Recipe instruction"
          />
        </div>
      </div>
    </InfoBox>
  );
};

export { RecipeDetailsCard };
